import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { onboardingService, OnbTemplateFolder, OnbProjectTemplate } from '../services/onboardingService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/CustomersList.css';
import '../styles/Pages.css';
import '../styles/InsightsPage.css';
import '../styles/OnboardingTemplates.css';

type TabKey = 'mine' | 'public';

const UNCATEGORIZED = '__uncat__';

const timeAgo = (iso: string | null): string => {
  if (!iso) return 'never';
  const d = new Date(iso).getTime();
  if (isNaN(d)) return '—';
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m} minute${m > 1 ? 's' : ''} ago`;
  const h = Math.floor(m / 60); if (h < 24) return `about ${h} hour${h > 1 ? 's' : ''} ago`;
  const day = Math.floor(h / 24); if (day < 30) return `${day} day${day > 1 ? 's' : ''} ago`;
  const mo = Math.floor(day / 30); if (mo < 12) return `${mo} month${mo > 1 ? 's' : ''} ago`;
  return `${Math.floor(mo / 12)} year(s) ago`;
};

const initials = (name?: string | null) => (name || 'U').trim().split(/\s+/).slice(0, 1).map(s => s[0]).join('').toUpperCase();

const OnboardingTemplates: React.FC = () => {
  const { currentOrg, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('mine');
  const [folders, setFolders] = useState<OnbTemplateFolder[]>([]);
  const [templates, setTemplates] = useState<OnbProjectTemplate[]>([]);
  const [activeFolder, setActiveFolder] = useState<string>(UNCATEGORIZED);
  const [search, setSearch] = useState('');
  const [sortAsc, setSortAsc] = useState(true);

  // folder rename inline
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderVal, setNewFolderVal] = useState('');
  const newFolderRef = useRef<HTMLInputElement>(null);

  // template delete
  const [confirmDelete, setConfirmDelete] = useState<OnbProjectTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [savingPublicId, setSavingPublicId] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState<string | null>(null);

  const ownerName = (currentOrg?.name || user?.user_metadata?.full_name || 'You') as string;

  const reload = async () => {
    try {
      const [fs, ts] = await Promise.all([
        onboardingService.listTemplateFolders(),
        onboardingService.listProjectTemplates(tab),
      ]);
      setFolders(fs);
      setTemplates(ts);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  useEffect(() => { if (currentOrg?.id) reload(); /* eslint-disable-next-line */ }, [currentOrg?.id, tab]);

  // Folder counts
  const countFor = (folderId: string) =>
    templates.filter(t => folderId === UNCATEGORIZED ? !t.folderId : t.folderId === folderId).length;

  const q = search.trim().toLowerCase();
  const visibleTemplates = useMemo(() => {
    let list = templates.filter(t => (activeFolder === UNCATEGORIZED ? !t.folderId : t.folderId === activeFolder));
    if (q) list = list.filter(t => t.name.toLowerCase().includes(q));
    list = [...list].sort((a, b) => sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
    return list;
  }, [templates, activeFolder, q, sortAsc]);

  const activeFolderName = activeFolder === UNCATEGORIZED ? 'Uncategorized' : (folders.find(f => f.id === activeFolder)?.name ?? 'Templates');

  // ── Folder actions ──
  const createFolder = async () => {
    const name = newFolderVal.trim();
    if (!name) { setCreatingFolder(false); return; }
    try {
      const f = await onboardingService.createTemplateFolder(name);
      setFolders(prev => [...prev, f]);
      setActiveFolder(f.id);
    } catch (err: any) { alert(`Failed to create folder: ${err?.message ?? err}`); }
    setCreatingFolder(false); setNewFolderVal('');
  };
  const saveRename = async (id: string) => {
    const name = renameVal.trim();
    if (name) {
      try { await onboardingService.renameTemplateFolder(id, name); setFolders(prev => prev.map(f => f.id === id ? { ...f, name } : f)); }
      catch (err: any) { alert(`Failed to rename: ${err?.message ?? err}`); }
    }
    setRenamingId(null);
  };
  const deleteFolder = async (f: OnbTemplateFolder) => {
    if (!window.confirm(`Delete folder "${f.name}"? Its templates move to Uncategorized.`)) return;
    try {
      await onboardingService.deleteTemplateFolder(f.id);
      setFolders(prev => prev.filter(x => x.id !== f.id));
      if (activeFolder === f.id) setActiveFolder(UNCATEGORIZED);
      reload();
    } catch (err: any) { alert(`Failed to delete folder: ${err?.message ?? err}`); }
  };

  // ── Template actions ──
  // Create a draft template row, then open the full builder page (same
  // navigation experience as forms/documents builders).
  const openCreate = async () => {
    if (creating) return;
    setCreating(true);
    const folderId = activeFolder === UNCATEGORIZED ? null : activeFolder;
    try {
      const t = await onboardingService.createProjectTemplate({ name: 'Untitled Template', folderId, isPublic: false });
      navigate(`/onboarding/templates/${t.id}/edit`);
    } catch (err: any) {
      alert(`Failed to create template: ${err?.message ?? err}`);
      setCreating(false);
    }
  };
  const openEdit = (t: OnbProjectTemplate) => navigate(`/onboarding/templates/${t.id}/edit`);
  // Public tab: copy a pre-built public template into "Your Templates".
  const saveToMine = async (t: OnbProjectTemplate) => {
    if (savingPublicId) return;
    setSavingPublicId(t.id);
    try {
      await onboardingService.saveTemplateToMine(t.id);
      setSavedToast(`“${t.name}” saved to Your Templates`);
      setTimeout(() => setSavedToast(null), 3000);
    } catch (err: any) {
      alert(`Failed to save template: ${err?.message ?? err}`);
    } finally {
      setSavingPublicId(null);
    }
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id; setConfirmDelete(null);
    setTemplates(prev => prev.filter(t => t.id !== id));
    try { await onboardingService.deleteProjectTemplate(id); } catch (err) { console.error(err); }
  };

  return (
    <Layout>
      <div className="customers-list-container">
        {/* Tabs (icon tabs, like Insights) */}
        <div className="customers-header-row">
          <div className="insights-tabs" role="tablist">
            <button role="tab" aria-selected={tab === 'mine'} className={`insights-tab ${tab === 'mine' ? 'active' : ''}`} onClick={() => setTab('mine')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tab === 'mine' ? '#4338ca' : '#6b7280'} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
              <span>Your Templates</span>
              <span className="insights-tab-count">{tab === 'mine' ? templates.length : ''}</span>
            </button>
            <button role="tab" aria-selected={tab === 'public'} className={`insights-tab ${tab === 'public' ? 'active' : ''}`} onClick={() => setTab('public')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tab === 'public' ? '#4338ca' : '#6b7280'} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></svg>
              <span>Public Templates</span>
              <span className="insights-tab-count">{tab === 'public' ? templates.length : ''}</span>
            </button>
          </div>
        </div>

        {tab === 'public' ? (
          <PublicTemplates
            templates={templates}
            onSave={saveToMine}
            onPreview={t => navigate(`/onboarding/templates/${t.id}/edit`)}
            savingId={savingPublicId}
          />
        ) : (
        <div className="onbt-body">
          {/* Folders sidebar */}
          <aside className="onbt-folders">
            <div className="onbt-folders-head">
              <span>Folders</span>
              {tab === 'mine' && (
                <button className="onbt-folder-add" title="Add folder" onClick={() => { setCreatingFolder(true); setTimeout(() => newFolderRef.current?.focus(), 30); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              )}
            </div>

            <button className={`onbt-folder ${activeFolder === UNCATEGORIZED ? 'active' : ''}`} onClick={() => setActiveFolder(UNCATEGORIZED)}>
              <span className="onbt-folder-ic"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2z"/></svg></span>
              <span className="onbt-folder-name">Uncategorized</span>
              <span className="onbt-folder-count">{countFor(UNCATEGORIZED)}</span>
            </button>

            {folders.map(f => (
              <div key={f.id} className={`onbt-folder ${activeFolder === f.id ? 'active' : ''}`} onClick={() => setActiveFolder(f.id)}>
                <span className="onbt-folder-ic"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2z"/></svg></span>
                {renamingId === f.id ? (
                  <input className="onbt-folder-rename" autoFocus value={renameVal} onClick={e => e.stopPropagation()} onChange={e => setRenameVal(e.target.value)} onBlur={() => saveRename(f.id)} onKeyDown={e => { if (e.key === 'Enter') saveRename(f.id); if (e.key === 'Escape') setRenamingId(null); }} />
                ) : (
                  <span className="onbt-folder-name">{f.name}</span>
                )}
                {tab === 'mine' && renamingId !== f.id && (
                  <span className="onbt-folder-actions" onClick={e => e.stopPropagation()}>
                    <button title="Rename" onClick={() => { setRenamingId(f.id); setRenameVal(f.name); }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button title="Delete" onClick={() => deleteFolder(f)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </span>
                )}
                {renamingId !== f.id && <span className="onbt-folder-count">{countFor(f.id)}</span>}
              </div>
            ))}

            {creatingFolder && (
              <div className="onbt-folder">
                <span className="onbt-folder-ic"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.7"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2z"/></svg></span>
                <input ref={newFolderRef} className="onbt-folder-rename" placeholder="Folder name" value={newFolderVal} onChange={e => setNewFolderVal(e.target.value)} onBlur={createFolder} onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') setCreatingFolder(false); }} />
              </div>
            )}
          </aside>

          {/* Main */}
          <main className="onbt-main">
            <div className="onbt-main-head">
              <h2 className="onbt-main-title">{activeFolderName} Templates</h2>
              <div className="onbt-main-actions">
                <div className="search-bar onbt-search">
                  <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                  <input type="text" placeholder="Search" value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
                </div>
                {tab === 'mine' && (
                  <button className="customers-add-btn btn-primary-cta" onClick={openCreate}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Create template
                  </button>
                )}
              </div>
            </div>

            <div className="onbt-table">
              <div className="onbt-row onbt-head">
                <button className="onbt-th onbt-c-name" onClick={() => setSortAsc(s => !s)}>Template Name <span className="onbt-sort">{sortAsc ? '▲' : '▼'}</span></button>
                <span className="onbt-th onbt-c-owner">Template Owner</span>
                <span className="onbt-th onbt-c-used">Used</span>
                <span className="onbt-th onbt-c-created">Created</span>
                <span className="onbt-th onbt-c-lastused">Last Used</span>
                <span className="onbt-th onbt-c-actions">Actions</span>
              </div>

              {visibleTemplates.map(t => (
                <div key={t.id} className="onbt-row">
                  <span className="onbt-c-name onbt-name">{t.name}</span>
                  <span className="onbt-c-owner onbt-owner"><span className="onbt-avatar">{initials(ownerName)}</span>{ownerName}</span>
                  <span className="onbt-c-used">{t.usedCount}</span>
                  <span className="onbt-c-created onbt-muted">{timeAgo(t.createdAt)}</span>
                  <span className="onbt-c-lastused onbt-muted">{t.lastUsedAt ? timeAgo(t.lastUsedAt) : '—'}</span>
                  <span className="onbt-c-actions onbt-actions">
                    {tab === 'mine' && <button className="onbt-btn-edit" onClick={() => openEdit(t)}>Edit</button>}
                    {tab === 'mine' && <button className="onbt-btn-del" title="Delete" onClick={() => setConfirmDelete(t)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>}
                    <button className="onbt-btn-create">Create Project</button>
                  </span>
                </div>
              ))}

              {visibleTemplates.length === 0 && (
                <div className="onbt-empty">No templates in this folder yet. Click “Create template” to add one.</div>
              )}
            </div>
          </main>
        </div>
        )}
      </div>

      {confirmDelete && (
        <div className="onbt-confirm-backdrop" onClick={() => setConfirmDelete(null)}>
          <div className="onbt-confirm-card" onClick={e => e.stopPropagation()}>
            <div className="onbt-confirm-icon"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg></div>
            <h3 className="onbt-confirm-title">Delete this template?</h3>
            <p className="onbt-confirm-text"><strong>{confirmDelete.name}</strong> will be permanently removed. This action can’t be undone.</p>
            <div className="onbt-confirm-actions">
              <button className="onbt-confirm-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="onbt-confirm-delete" onClick={doDelete}>Delete template</button>
            </div>
          </div>
        </div>
      )}

      {savedToast && (
        <div className="onbt-toast">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          {savedToast}
        </div>
      )}
    </Layout>
  );
};

/* ── Public templates gallery (Explore Public Templates) ────── */
const PublicTemplates: React.FC<{
  templates: OnbProjectTemplate[];
  onSave: (t: OnbProjectTemplate) => void;
  onPreview: (t: OnbProjectTemplate) => void;
  savingId: string | null;
}> = ({ templates, onSave, onPreview, savingId }) => (
  <div className="onbt-public">
    <div className="onbt-public-head">
      <h2 className="onbt-public-title">Explore Public Templates</h2>
      <p className="onbt-public-sub">Save time by starting with one of our pre-built templates to kickstart your project.</p>
    </div>

    {templates.length === 0 ? (
      <div className="onbt-empty">No public templates are available yet.</div>
    ) : (
      <div className="onbt-public-grid">
        {templates.map(t => (
          <div key={t.id} className="onbt-public-card">
            <div className="onbt-public-card-head">{t.name}</div>
            <div className="onbt-public-card-body">
              <p className={`onbt-public-desc ${t.description ? '' : 'muted'}`}>{t.description || 'No description'}</p>
            </div>
            <div className="onbt-public-card-actions">
              <button className="onbt-public-preview" onClick={() => onPreview(t)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>
                Preview
              </button>
              <button className="onbt-btn-create" disabled={savingId === t.id} onClick={() => onSave(t)}>
                {savingId === t.id ? 'Saving…' : 'Save Template'}
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default OnboardingTemplates;
